"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";

const PanelWorkspaceContext = createContext({
  workspace: null,
  isCustomDomain: false,
});

const parseCookie = (raw) => {
  if (!raw) return null;
  try {
    const value = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!value || typeof value !== "object" || !value.orgId) return null;
    return value;
  } catch {
    return null;
  }
};

/** The middleware's `x-panel-workspace` request header: the same payload, URI-encoded. */
export const parseWorkspaceHeader = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  try {
    return parseCookie(decodeURIComponent(raw));
  } catch {
    return null;
  }
};

/**
 * Exposes the tenant resolved from the request hostname by the middleware.
 * On the canonical `panel.surveyanalytica.com` host (and localhost), neither
 * source is set and `workspace` is null — callers should treat the session
 * as global, same as before this feature landed.
 *
 * `initial` is the tenant as the server resolved it for THIS document (root layout reads
 * the middleware's request header), so the first server HTML already carries the portal's
 * logo and name. The cookie only refreshes it: the middleware rewrites the cookie on every
 * navigation, so a logo changed in the builder arrives without a reload. The cookie lives
 * 15 minutes, and its expiry says nothing about the tenant — a host does not stop being a
 * portal because a tab sat idle — so a missing cookie never clears the workspace. It used
 * to: the tenant dropped to null, PortalProvider stopped fetching, and every logo fell
 * through to the signed-in org's picture.
 */
export const PanelWorkspaceProvider = ({ children, initial }) => {
  const [cookies] = useCookies(["panel-workspace"]);
  const [workspace, setWorkspace] = useState(
    () =>
      parseWorkspaceHeader(initial) || parseCookie(cookies["panel-workspace"]),
  );

  useEffect(() => {
    const next = parseCookie(cookies["panel-workspace"]);
    if (next) setWorkspace(next);
  }, [cookies]);

  const value = useMemo(
    () => ({ workspace, isCustomDomain: !!workspace }),
    [workspace],
  );

  return (
    <PanelWorkspaceContext.Provider value={value}>
      {children}
    </PanelWorkspaceContext.Provider>
  );
};

export const usePanelWorkspace = () => useContext(PanelWorkspaceContext);
